/**
 * Bridges storefront interactions to whatever analytics snippet the tenant
 * injected via company/store settings — gtag.js or a GTM dataLayer. No-ops
 * when neither is present.
 *
 * Tenants are documented (guides/how-tos/storefront-google-analytics.md) to
 * disable automatic pageviews (send_page_view: false, no History Change
 * trigger) — this module is the single source of page_view events.
 */

// GA4 ecommerce fields. gtag wants these as event params; GTM's GA4 Event
// tag reads them from `ecommerce` when "Send ecommerce data" is on.
const ECOMMERCE_KEYS = [
  "items",
  "currency",
  "value",
  "transaction_id",
  "tax",
  "shipping",
  "coupon",
  "item_list_id",
  "item_list_name",
];

function splitEcommerce(params) {
  const ecommerce = {};
  const rest = { ...params };

  for (const key of ECOMMERCE_KEYS) {
    if (rest[key] != null) {
      ecommerce[key] = rest[key];
      delete rest[key];
    }
  }

  return { ecommerce, rest };
}

function track(event, params = {}) {
  if (typeof window.gtag === "function") {
    // gtag.js takes ecommerce fields (currency, value, items...) as flat params.
    window.gtag("event", event, params);
  } else if (Array.isArray(window.dataLayer)) {
    const { ecommerce, rest } = splitEcommerce(params);

    // Always reset first. GTM reuses the last `ecommerce` object when a later
    // event omits it, so a page_view after purchase would keep `transaction_id`.
    window.dataLayer.push({ ecommerce: null });

    if (Object.keys(ecommerce).length > 0) {
      window.dataLayer.push({ event, ...rest, ecommerce });
    } else {
      window.dataLayer.push({ event, ...rest });
    }
  }
}

// Sign-in URLs carry the phone number: /sessions/new/<phone>,
// /checkout/<id>/login/<phone>, and .../verify/<phone>. GA must never
// receive it, so the segment after those prefixes is dropped.
const PHONE_PATH = /\/(sessions\/new|login|verify)\/[^/]+/;

function pageLocation() {
  const url = new URL(window.location.href);
  url.pathname = url.pathname.replace(PHONE_PATH, "/$1");
  return url.toString();
}

function pageView() {
  track("page_view", {
    page_location: pageLocation(),
    page_title: document.title,
  });
}

// Server events that carry `once_key` fire at most once per browser tab.
// Checkout re-mounts after sign-in (a full page load) and on reconnects, and
// a purchase must never count twice. If storage is blocked, the event fires.
function alreadySent(onceKey) {
  if (!onceKey) return false;

  const storageKey = `analytics:once:${onceKey}`;

  try {
    if (window.sessionStorage.getItem(storageKey)) return true;
    window.sessionStorage.setItem(storageKey, "1");
  } catch (_e) {
    // Private mode or blocked storage: fall through and send.
  }

  return false;
}

export function initAnalytics() {
  // Server-pushed events (add_to_cart, view_item, ...).
  window.addEventListener("phx:analytics", (e) => {
    const { event, once_key: onceKey, ...params } = e.detail || {};
    if (event && !alreadySent(onceKey)) track(event, params);
  });

  // Declarative click tracking: any element with data-analytics-event.
  document.addEventListener("click", (e) => {
    const el = e.target.closest("[data-analytics-event]");
    if (el) track("nav_click", { nav_item: el.dataset.analyticsEvent });
  });

  // SPA pageviews: initial load, then every live navigation that changes the
  // *path*. Query-only patches (filters, bag, modals) are UI state, not pages.
  let lastPath = window.location.pathname;
  pageView();

  window.addEventListener("phx:navigate", () => {
    if (window.location.pathname !== lastPath) {
      lastPath = window.location.pathname;
      pageView();
    }
  });
}
